// src/App.js
import React, { useEffect } from "react";
import { IS_DEV } from "./utils/env";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "./context/themeContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/* ==== Notificações (store + SSE) ==== */
import { NotifyProvider, useNotify } from "./context/notifyStore";
import { initStream } from "./utils/streamClient";

/* ==== Páginas atuais ==== */
import LoginScreen from "./pages/loginScreen";
import Chamados from "./pages/chamados";
import Agendamentos from "./pages/agendamentosScreen";
import CadastroTela from "./pages/cadastroScreen";
import CadastroFunc from "./pages/cadastroFuncScreen";
import Dashboard from "./pages/dashboard";
import RegraDeImposto from "./pages/regrasDeImposto";
import PontoScreen from "./pages/pontoScreen";
import ErrosScreen from "./pages/IncidentesScreen";
import PrivateRoute from "./components/privateRoute";
import MessageRoute from "./pages/sendMessage";
import Projetos from "./pages/ProjetosAdmin";
import MinhaFila from "./pages/MinhaFilaDev";
import CadastroChecklist from "./pages/ChecklistTemplateCreate";
import ExecucaoChecklist from "./pages/ExecutarChecklist";
import AprovarChecklist from "./pages/AprovarChecklists";
import Leads from "./pages/LeadsList";
import LeadDetail from "./pages/LeadDetail";
import LeadNew from "./pages/LeadNew";
import Cobranca from "./pages/finance";
import Servicos from "./pages/servicosAdicionais";
import ContasPagar from "./pages/ContasPagar";
import FinanceDash from "./pages/FinanceDash";
import GestaoPonto from "./pages/GestaoPontoScreen";
import ReleasesTestes from "./pages/ReleasesTestes";
import TransferLicenca from "./pages/transferenciaLicenca";
import Ibpt from "./pages/IbptPage";
import GestaoProjetos from "./pages/GestaoProjetos";
import SolicitarProjeto from "./pages/SolicitarProjeto";
import DashboardWhatsApp from "./pages/DashboardCobrancaWhatsapp";
import FiscalNfse from "./pages/FiscalNfse";
import SaneamentoFiscal from "./pages/saneamentoFiscal";
import GestaoContratosPage from "./pages/GestaoContratosPage.jsx";
import AssinarContratoPage from "./pages/AssinarContratoPage.jsx";

/* ==== Novas telas ==== */
import MensagensInternas from "./pages/mensagensInternas";
import AgendaLembretes from "./pages/agendaLembretes";

/* ==== WebSocket Provider ==== */
import SocketProvider from "./ws/SocketProvider";

/* ==== Listener global de mensagens internas ==== */
import MsgNotifyListener from "./components/MsgNotifyListener";

/* ===== Helper: user logado ===== */
/* ===== Helper: user logado ===== */
function getLoggedUserId() {
  const KEYS = [
    "IdFuncionario",
    "idFuncionario",
    "CodCadFunc",
    "codFunc",
    "DevId",
    "devId",
    "DEV_ID",
    "userId",
    "id",
    "SSW_MSG_USER",
  ];

  for (const k of KEYS) {
    const raw = sessionStorage.getItem(k) ?? localStorage.getItem(k);

    if (!raw) continue;

    const n = Number(raw);
    if (Number.isInteger(n) && n > 0) {
      return n;
    }
  }

  return null;
}

/* ===== Boot do SSE: apenas eventos de Agenda (evita conflito com WS) ===== */
function SseBoot({ children }) {
  const { pushAviso, incBadge } = useNotify();

  useEffect(() => {
    const uid = getLoggedUserId();
    if (!uid) {
      if (IS_DEV) console.log("[SSE] sem userId, não iniciando stream");
      return;
    }

    const stop = initStream(uid, (type, payload) => {
      // DEBUG: ver exatamente o que o backend está mandando
      if (IS_DEV) {
        console.log("[SSE] evento recebido:", { type, payload });
      }

      // Mensagens internas continuam 100% pelo Socket.IO
      if (type === "NEW_MESSAGE") return;

      // Aceita diferentes “nomes” para eventos de agenda
      const isAgendaEvent =
        type === "AGENDA_REMINDER" ||
        type === "AGENDA" ||
        type === "AGENDA_NOTIFY" ||
        type === "AGENDA_LEMBRETE";

      if (!isAgendaEvent) {
        // Só pra enxergar se tiver outro tipo estranho vindo
        if (IS_DEV) {
          console.log("[SSE] tipo ignorado no SseBoot:", type);
        }
        return;
      }

      // Sobe badge da agenda
      incBadge("agenda");

      // Tenta achar um id decente no payload
      const itemId =
        payload?.itemId ??
        payload?.id ??
        payload?.Id ??
        payload?.ID ??
        Date.now();

      const ts = Date.now();

      // Empilha aviso no notifyStore -> sidebar
      pushAviso({
        id: `agd-${itemId}-${ts}`,
        tipo: "AGENDA_REMINDER",
        ts,
        payload,
        lido: false,
        cta: {
          label: "Ver item",
          to: `/agenda?focus=${itemId}`,
        },
      });
    });

    return stop; // encerra EventSource ao desmontar
  }, [pushAviso, incBadge]);

  // Silencia erros de rede de background em DEV (mantém o que você já tinha)
  useEffect(() => {
    if (!IS_DEV) return;
    const onUR = (e) => {
      const r = e?.reason || {};
      const isNet =
        r?.code === "ERR_NETWORK" ||
        r?.message === "Network Error" ||
        (r?.isAxiosError && !r?.response);
      if (isNet) {
        e.preventDefault();
        console.debug("Network error silenced (background/polling):", r);
      }
    };
    window.addEventListener("unhandledrejection", onUR);
    return () => window.removeEventListener("unhandledrejection", onUR);
  }, []);

  return children;
}

const App = () => {
  const userId = getLoggedUserId(); // fonte única da verdade

  return (
    <ThemeProvider>
      <NotifyProvider>
        <SocketProvider userId={userId}>
          {/* Listener global de mensagens internas */}
          <MsgNotifyListener />

          <SseBoot>
            <Router>
              <ToastContainer
                position="bottom-right"
                autoClose={3500}
                pauseOnHover
                draggable
                newestOnTop
                limit={3}
              />

              <Routes>
                <Route path="/" element={<LoginScreen />} />
                <Route path="/assinar/:token" element={<AssinarContratoPage />} />

                <Route
                  path="/dashboard"
                  element={
                    <PrivateRoute>
                      <Dashboard />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/chamados"
                  element={
                    <PrivateRoute>
                      <Chamados />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/agendamentos"
                  element={
                    <PrivateRoute>
                      <Agendamentos />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/cadastros"
                  element={
                    <PrivateRoute>
                      <CadastroTela />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/contratos"
                  element={
                    <PrivateRoute>
                      <GestaoContratosPage />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/cadastros/funcionarios"
                  element={
                    <PrivateRoute>
                      <CadastroFunc />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/regras-de-imposto"
                  element={
                    <PrivateRoute>
                      <RegraDeImposto />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/ibpt"
                  element={
                    <PrivateRoute>
                      <Ibpt />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/releases/testes"
                  element={
                    <PrivateRoute>
                      <ReleasesTestes />
                    </PrivateRoute>
                  }
                />

                {/* Rota existente de mensagens */}
                <Route
                  path="/mensagens"
                  element={
                    <PrivateRoute>
                      <MessageRoute />
                    </PrivateRoute>
                  }
                />

                {/* Novas rotas */}
                <Route
                  path="/mensagens-internas"
                  element={
                    <PrivateRoute>
                      <MensagensInternas />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/agenda"
                  element={
                    <PrivateRoute>
                      <AgendaLembretes />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/fiscal/nfe"
                  element={
                    <PrivateRoute>
                      <FiscalNfse />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/solicitarProjeto"
                  element={
                    <PrivateRoute>
                      <SolicitarProjeto />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/erros"
                  element={
                    <PrivateRoute>
                      <ErrosScreen />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/gestaoProjetos"
                  element={
                    <PrivateRoute>
                      <GestaoProjetos />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/minhaFila"
                  element={
                    <PrivateRoute>
                      <MinhaFila />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/projetos"
                  element={
                    <PrivateRoute>
                      <Projetos />
                    </PrivateRoute>
                  }
                />

                {/* Leads */}
                <Route
                  path="/leads/new"
                  element={
                    <PrivateRoute>
                      <LeadNew />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/leads/:id"
                  element={
                    <PrivateRoute>
                      <LeadDetail />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/leads"
                  element={
                    <PrivateRoute>
                      <Leads />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/servicos"
                  element={
                    <PrivateRoute>
                      <Servicos />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/saneamentofiscal"
                  element={
                    <PrivateRoute>
                      <SaneamentoFiscal />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/cadastroChecklist"
                  element={
                    <PrivateRoute>
                      <CadastroChecklist />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/transferenciaDeDados"
                  element={
                    <PrivateRoute>
                      <TransferLicenca />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/execucaoChecklist"
                  element={
                    <PrivateRoute>
                      <ExecucaoChecklist />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/aprovarChecklist"
                  element={
                    <PrivateRoute>
                      <AprovarChecklist />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/financeiro/cobranca"
                  element={
                    <PrivateRoute>
                      <Cobranca />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/financeiro/dashboardWhatsApp"
                  element={
                    <PrivateRoute>
                      <DashboardWhatsApp />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/financeiro/contas-pagar"
                  element={
                    <PrivateRoute>
                      <ContasPagar />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/gestao/Ponto"
                  element={
                    <PrivateRoute>
                      <GestaoPonto />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/financeiro/dashboard"
                  element={
                    <PrivateRoute>
                      <FinanceDash />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/ponto"
                  element={
                    <PrivateRoute>
                      <PontoScreen />
                    </PrivateRoute>
                  }
                />
              </Routes>
            </Router>
          </SseBoot>
        </SocketProvider>
      </NotifyProvider>
    </ThemeProvider>
  );
};

export default App;
